import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Form,
  Button,
  Row,
  Col,
  Container,
  Alert,
  Modal,
  Table,
} from "react-bootstrap";
import Select from "react-select"; // Import react-select

const TemplateTypeManager = () => {
  const [templateTypes, setTemplateTypes] = useState([]);
  const [templates, setTemplates] = useState([]); // To store fetched templates
  const [colors, setColors] = useState([]); // Colors state
  const [tId, setTId] = useState(""); // Template ID
  const [title, setTitle] = useState("");
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [selectedColors, setSelectedColors] = useState([]); // For multiple colors
  const [prices, setPrices] = useState([""]); // Array to hold multiple prices
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false); // To toggle between Add and Edit mode
  const [editId, setEditId] = useState(null); // To store the ID of the template being edited
  const [frontImagePreview, setFrontImagePreview] = useState(null); // Image preview
  const [backImagePreview, setBackImagePreview] = useState(null); // Image preview

  useEffect(() => {
    fetchTemplateTypes();
    fetchColors();
    fetchTemplates(); // Fetch the templates when the component loads
  }, []);

  // Fetch template types from the API
  const fetchTemplateTypes = async () => {
    try {
      const response = await axios.get(
        "https://drdprints.com/api/get-templatetypes"
      );
      setTemplateTypes(response.data.template_types || []); // Ensure it's an array
    } catch (err) {
      setError("Failed to fetch template types");
      console.error(err);
    }
  };

  // Fetch product colors from the API
  const fetchColors = async () => {
    try {
      const response = await axios.get(
        "https://drdprints.com/api/get-productcolors"
      );
      const colorOptions = response.data.productcolors.map((color) => ({
        value: color.color_code,
        label: color.color_name,
      }));
      setColors(colorOptions);
    } catch (error) {
      console.error("Error fetching colors:", error);
    }
  };

  // Fetch templates from the API
  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        "https://drdprints.com/api/get-templates"
      );
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Error fetching templates", error);
      setMessage("Error fetching templates. Please try again.");
      setError("danger");
    } finally {
      setLoading(false);
    }
  };

  // Handle image uploads and set preview
  const handleFrontImageUpload = (event) => {
    const file = event.target.files[0];
    setFrontImage(file);
    setFrontImagePreview(URL.createObjectURL(file)); // Set image preview
  };

  const handleBackImageUpload = (event) => {
    const file = event.target.files[0];
    setBackImage(file);
    setBackImagePreview(URL.createObjectURL(file)); // Set image preview
  };

  // Handle adding new price input field
  const handleAddPrice = () => {
    setPrices([...prices, ""]);
  };

  // Handle price input change
  const handlePriceChange = (index, value) => {
    const updatedPrices = [...prices];
    updatedPrices[index] = value;
    setPrices(updatedPrices);
  };

  // Handle multi-select color change using react-select
  const handleColorChange = (selectedOptions) => {
    setSelectedColors(selectedOptions || []);
  };

  // Handle deleting template type by ID
  const handleDeleteTemplateType = async (id) => {
    try {
      await axios.delete(
        `https://drdprints.com/api/deletetemplatetype/${id}`
      );
      setMessage("Template Type deleted successfully!");
      fetchTemplateTypes(); // Refresh template types after deletion
    } catch (error) {
      console.error("Error deleting template type:", error);
      setError("Failed to delete Template Type.");
    }
  };

  const handleEditTemplateType = (template) => {
    setIsEditMode(true);
    setEditId(template.id);
    setTId(template.template_id); // Ensure the template ID is set for editing
    setTitle(template.title); // Ensure the title is set for editing
    setSelectedColors(
      template.color.map((c) => ({ value: c.color, label: c.color }))
    ); // Pre-fill colors
    setPrices(template.quantity.map((q) => q.prices)); // Pre-fill prices
    setFrontImagePreview(template.front_image ? `https://drdprints.com/uploads/templates/${template.front_image}` : null); // Set existing front image preview
    setBackImagePreview(template.back_image ? `https://drdprints.com/uploads/templates/${template.back_image}` : null); // Set existing back image preview
    setShowModal(true); // Show modal
  };
  

  const handleUpdateTemplateType = async (e) => {
    e.preventDefault();
    
    const payload = {
        t_id: tId, // Ensure this matches what the API expects
        title: title,
        color: selectedColors.map((color) => color.value).join(", "),
        prices: prices.join(", "),
    };

    console.log("Payload: ", payload); // Log payload to ensure it's correct

    try {
        const response = await axios.put(
            `https://drdprints.com/api/update-templatetype/${editId}`,
            payload, 
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        );

        console.log(response.data);
        setMessage("Template Type updated successfully!");
        // Clear fields after update
        setIsEditMode(false);
        setEditId(null);
        setTId(""); 
        setTitle("");
        setSelectedColors([]);
        setPrices([""]);
        setShowModal(false);
        fetchTemplateTypes(); // Refresh template types
    } catch (error) {
        console.error("Error updating template type:", error.response ? error.response.data : error);
        setError("Failed to update Template Type.");
    }
};


  

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("t_id", tId); // Ensure template ID is included in formData
    formData.append("title", title); // Ensure title is included in formData
    formData.append("front_image", frontImage);
    formData.append("back_image", backImage);

    // Convert selectedColors to a comma-separated string
    const selectedColorValues = selectedColors
      .map((color) => color.value)
      .join(", ");
    formData.append("color", selectedColorValues); // Append as a string

    // Convert prices to a comma-separated string
    const priceValues = prices.join(", ");
    formData.append("prices", priceValues); // Append as a string

    try {
      const response = await axios.post(
        "https://drdprints.com/api/store-templatetype",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setMessage("Template Type added successfully!");
      // Clear fields
      setTId(""); // Clear template ID
      setTitle(""); // Clear title
      setFrontImage(null);
      setBackImage(null);
      setFrontImagePreview(null); // Clear image previews
      setBackImagePreview(null);
      setSelectedColors([]);
      setPrices([""]);
      setShowModal(false); // Close modal after submission
      fetchTemplateTypes(); // Refresh template types
    } catch (error) {
      console.error("There was an error adding the template type!", error);
      setMessage("Failed to add Template Type.");
    }
  };

  return (
    <Container>
      <h2 className="my-4 text-center">Manage Template Types</h2>

      <Row className="justify-content-center">
        <Col xs={12} md={12}>
          <Button
            variant="primary"
            onClick={() => {
              setIsEditMode(false);
              setShowModal(true);
            }}
            className="mb-3"
          >
            Add Template Type
          </Button>

          {/* Display fetched template types in a table */}
          <Table
            striped
            bordered
            hover
            responsive="md"
            className="custom-table"
          >
            <thead>
              <tr>
                <th>ID</th>
                <th>Title</th>
                <th>Front Image</th>
                <th>Back Image</th>
                <th>Colors</th>
                <th>Prices</th>
                <th>Actions</th> {/* New Actions Column */}
              </tr>
            </thead>
            <tbody>
              {templateTypes.length > 0 &&
                templateTypes.map((template) => (
                  <tr key={template.id}>
                    <td>{template.id}</td>
                    <td>{template.title}</td>
                    <td>
                      {template.front_image ? (
                        <img
                          src={`https://drdprints.com/uploads/templates/${template.front_image}`}
                          alt={template.title}
                          width="100"
                          className="img-fluid"
                        />
                      ) : (
                        "No image"
                      )}
                    </td>
                    <td>
                      {template.back_image ? (
                        <img
                          src={`https://drdprints.com/uploads/templates/${template.back_image}`}
                          alt={template.title}
                          width="100"
                          className="img-fluid"
                        />
                      ) : (
                        "No image"
                      )}
                    </td>
                    <td>
                      {template.color.length > 0
                        ? template.color.map((c) => c.color).join(", ")
                        : "No colors available"}
                    </td>
                    <td>
                      {template.quantity.length > 0
                        ? template.quantity.map((q) => q.prices).join(", ")
                        : "No prices available"}
                    </td>
                    <td>
                      <Button
                        variant="warning"
                        onClick={() => handleEditTemplateType(template)}
                        className="mb-2"
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        onClick={() => handleDeleteTemplateType(template.id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>

          {/* Success/Failure messages */}
          {message && <Alert variant="success">{message}</Alert>}
          {error && <Alert variant="danger">{error}</Alert>}
        </Col>
      </Row>

      {/* Modal for adding/updating template type */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditMode ? "Edit Template Type" : "Add Template Type"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={isEditMode ? handleUpdateTemplateType : handleSubmit}>
            {/* Template Dropdown */}
            <Form.Group controlId="formTemplateID">
              <Form.Label>Select Template</Form.Label>
              <Form.Control
                as="select"
                value={tId}
                onChange={(e) => setTId(e.target.value)}
                required
              >
                <option value="">Select a Template</option>
                {templates.length > 0 &&
                  templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.template_name}
                    </option>
                  ))}
              </Form.Control>
            </Form.Group>

            {/* Title */}
            <Form.Group controlId="formTitle" className="mt-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter template title"
                required
              />
            </Form.Group>

            {/* Front Image */}
            <Form.Group controlId="formFrontImage" className="mt-3">
              <Form.Label>Front Image</Form.Label>
              <Form.Control type="file" onChange={handleFrontImageUpload} />
              {frontImagePreview && (
                <img
                  src={frontImagePreview}
                  alt="Front Preview"
                  className="img-fluid mt-2"
                  width="100"
                />
              )}
            </Form.Group>

            {/* Back Image */}
            <Form.Group controlId="formBackImage" className="mt-3">
              <Form.Label>Back Image</Form.Label>
              <Form.Control type="file" onChange={handleBackImageUpload} />
              {backImagePreview && (
                <img
                  src={backImagePreview}
                  alt="Back Preview"
                  className="img-fluid mt-2"
                  width="100"
                />
              )}
            </Form.Group>

            {/* Colors Dropdown (Multiple Selection) using react-select */}
            <Form.Group controlId="formColors" className="mt-3">
              <Form.Label>Colors</Form.Label>
              <Select
                isMulti
                options={colors} // Use fetched colors from API
                value={selectedColors}
                onChange={handleColorChange} // Updates selected colors on change
                classNamePrefix="select"
              />
            </Form.Group>

            {/* Prices */}
            <Form.Group controlId="formPrices" className="mt-3">
              <Form.Label>Prices</Form.Label>
              {prices.map((price, index) => (
                <Form.Control
                  key={index}
                  type="number"
                  value={price}
                  onChange={(e) => handlePriceChange(index, e.target.value)}
                  placeholder={`Price ${index + 1}`}
                  required
                  className="mb-2"
                />
              ))}
              <Button variant="info" onClick={handleAddPrice} className="mb-2">
                Add Another Price
              </Button>
            </Form.Group>

            <Button variant="primary" type="submit">
              {isEditMode ? "Update" : "Submit"}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default TemplateTypeManager;
